import type { IInterpolationCache, ILangProvider, ILanguageInterpolator } from './interfaces';

export interface ILocalizeContent {
  id: string;
  p?: Record<string, string | number>;
}

export class LanguageInterpolator implements ILanguageInterpolator {
  private readonly langProvider: ILangProvider;
  private readonly interpolationCache: IInterpolationCache;

  constructor(langProvider: ILangProvider, interpolationCache: IInterpolationCache) {
    this.langProvider = langProvider;
    this.interpolationCache = interpolationCache;
  }

  private readonly translationHeader = '$#TRANSLATION:';
  private readonly localizeHeader = '$#LOCALIZE:';
  private readonly translationHeaderLength = this.translationHeader.length;
  private readonly localizeHeaderLength = this.localizeHeader.length;

  private isValueATranslationReference(value: string): boolean {
    return (
      value.startsWith(this.translationHeader) &&
      value.substring(this.translationHeaderLength, this.translationHeaderLength + 3) === 'id='
    );
  }

  private isValueALocalizeReference(value: string): boolean {
    return value.startsWith(this.localizeHeader);
  }

  private convertRawValueToString(value: string, resourceCollectionName?: string): string {
    const externalValue = this.langProvider.getEntryByValue(value.replace('  ', ' '), resourceCollectionName);

    if (!externalValue.includes('{{glossary')) {
      return externalValue;
    }
    return this.interpolationCache.getRawInterpolated(this.langProvider.currentLanguage, externalValue);
  }

  private localizeValue(translationReference: string, resourceCollectionName?: string): string {
    const jsonString = translationReference.substring(this.localizeHeaderLength);
    try {
      const localizeContent: ILocalizeContent = JSON.parse(jsonString);
      // Parse parameters into an object
      const parameters: Record<string, string> = {};
      if (localizeContent.p) {
        Object.entries(localizeContent.p).forEach(([key, value]) => {
          if (typeof value === 'string') {
            if (value.startsWith('$raw:')) {
              // If the value starts with $raw:, we treat it as a raw value
              parameters[key] = this.convertRawValueToString(value.substring(5), resourceCollectionName);
            } else if (this.isValueALocalizeReference(value)) {
              parameters[key] = this.localizeValue(value, resourceCollectionName);
            } else parameters[key] = value;
          } else {
            parameters[key] = value.toString();
          }
        });
      }
      // Lookup translation entry
      const entry = this.langProvider.getEntry(localizeContent.id, null);

      // Interpolate string with parameters
      const result = this.interpolationCache.getInterpolated(
        this.langProvider.currentLanguage,
        localizeContent.id,
        '__default__',
        entry,
        parameters
      );

      return result;
    } catch (e) {
      // Handle invalid JSON
      return '';
    }
  }

  public getExternalInterpolatedValue(translationReference: string, resourceCollectionName?: string): string {
    let idPart = '';
    let paramsPart = '';
    let result = '';

    if (this.isValueALocalizeReference(translationReference)) {
      return this.localizeValue(translationReference, resourceCollectionName);
    }
    // Validate the translation reference header
    if (!this.isValueATranslationReference(translationReference)) {
      return this.convertRawValueToString(translationReference, resourceCollectionName);
    }

    // Split the reference into ID and parameters
    const pipeIndex = translationReference.indexOf('|');
    if (pipeIndex > -1) {
      idPart = translationReference.substring(this.translationHeaderLength, pipeIndex);
      paramsPart = translationReference.substring(pipeIndex + 1);
    } else {
      idPart = translationReference.substring(this.translationHeaderLength);
      paramsPart = '';
    }

    // Remove 'id=' prefix from the ID
    if (idPart.startsWith('id=')) {
      idPart = idPart.substring(3);
    }

    // Parse parameters into an object
    const parameters: Record<string, string> = {};
    if (paramsPart) {
      paramsPart.split('|').forEach(pair => {
        const [key, value] = pair.split('=');
        if (key && value !== undefined) {
          if (value.startsWith('$raw:')) {
            // If the value starts with $raw:, we treat it as a raw value
            parameters[key] = this.convertRawValueToString(value.substring(5), resourceCollectionName);
          } else parameters[key] = value;
        }
      });
    }

    // Lookup translation entry
    const entry = this.langProvider.getEntry(idPart, null);

    // Interpolate string with parameters
    result = this.interpolationCache.getInterpolated(
      this.langProvider.currentLanguage,
      idPart,
      '__default__',
      entry,
      parameters
    );

    return result;
  }

  public getInterpolated(
    localizedId: string,
    parameters: object | null = null,
    segmentName: string | null = null
  ): string {
    const entry = this.langProvider.getEntry(localizedId, segmentName);

    if (parameters === null && !entry.includes('{{glossary')) {
      return entry;
    }

    return this.interpolationCache.getInterpolated(
      this.langProvider.currentLanguage,
      localizedId,
      segmentName ?? '__default__',
      entry,
      parameters ?? {}
    );
  }
}
